import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import type { TruncationMode } from '../../../typography/text-ellipsis/TextEllipsis.js';
import { charsFontWidth12pixelsSize400Map } from '../../core/components/text-ellipsis/chars-font-width-map.js';
import { truncateString } from '../../core/components/text-ellipsis/truncate-string.js';
import { getColorByContrast } from '../../core/utils/color-contrast.js';
import { COLUMN_LABEL_HORIZONTAL_PADDING } from '../constants.js';
import {
  columnHeaderItem,
  columnHeaderLabel,
  columnHeaderPin,
} from './SankeyColumnHeaders.sty.js';

interface SankeyColumnHeaderItemProps {
  label: string;
  color: string | undefined;
  truncationMode: TruncationMode;
  /** Left edge of the territory reserved for this column header, relative to the chart. */
  territoryLeft: number;
  /** Width of the territory reserved for this column header. */
  territoryWidth: number;
  /** Horizontal position of the column's nodes, relative to the chart. */
  nodeCenter: number;
}

/**
 * @internal
 */
export function SankeyColumnHeaderItem({
  label,
  color,
  truncationMode,
  territoryLeft,
  territoryWidth,
  nodeCenter,
}: Readonly<SankeyColumnHeaderItemProps>) {
  const pinLeft = nodeCenter - territoryLeft;
  const availableWidth = Math.max(
    0,
    2 * Math.min(pinLeft, territoryWidth - pinLeft) -
      COLUMN_LABEL_HORIZONTAL_PADDING,
  );
  const displayLabel = truncateString(
    label,
    availableWidth,
    charsFontWidth12pixelsSize400Map,
    truncationMode,
  );
  const isTruncated = displayLabel !== label;

  return (
    <div
      className={columnHeaderItem}
      style={{ left: territoryLeft, width: territoryWidth }}
    >
      <div className={columnHeaderPin} style={{ left: pinLeft }}>
        <Tooltip text={label} disabled={!isTruncated}>
          <div
            className={columnHeaderLabel}
            style={
              color !== undefined
                ? { background: color, color: getColorByContrast(color) }
                : undefined
            }
          >
            {displayLabel}
          </div>
        </Tooltip>
      </div>
    </div>
  );
}

SankeyColumnHeaderItem['displayName'] = 'SankeyColumnHeaderItem';
